import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { ArrowLeft, Send, Check, CheckCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import type {
  MessagingMessage,
  MessagingPage,
  SendMessagingMessagePayload,
} from '@gokrew/models/messaging';
import { Button } from '@/components/Button';
import { formatDate } from '@/utility/utility';
import { useMessaging } from '@/messaging/useMessaging';
import {
  compareMessagingSequence,
  mergeMessagingMessages,
  messagingDraft,
  messagingBubbleIsVisible,
  readMessagingDraft,
  saveMessagingDraft,
  isKnownMessagingRejection,
} from '@/messaging/state';
import styles from './MessagingInbox.module.css';
import { EmojiPicker } from './EmojiPicker';

export const ConversationThread = ({
  id,
  onBack,
}: {
  id: string;
  onBack: () => void;
}) => {
  const { t, i18n } = useTranslation(TranslationNS.LibsCommon);
  const { api, scope, bootstrap, settings, connection } = useMessaging();
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: [QueryKeys.MessagingConversations, scope, 'detail', id],
    queryFn: () => api.conversation(id),
    refetchInterval: 10_000,
    retry: 1,
    throwOnError: false,
  });
  const historyKey = useMemo(
    () => [QueryKeys.MessagingMessages, scope, id],
    [scope, id]
  );
  const history = useInfiniteQuery({
    queryKey: historyKey,
    queryFn: ({ pageParam }) =>
      api.messages(id, { before: pageParam ?? undefined, limit: 40 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 15_000,
    retry: 1,
    throwOnError: false,
  });
  const rows = useMemo(
    () =>
      mergeMessagingMessages(
        history.data?.pages.flatMap((page) => page.items) ?? []
      ),
    [history.data]
  );
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const scroll = useRef<HTMLDivElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const atBottom = useRef(true);
  const readThrough = useRef('0');
  const [stored] = useState(() => readMessagingDraft(scope, id));
  const [text, setText] = useState(stored.text);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const submission = useRef<SendMessagingMessagePayload | undefined>(
    stored.pending
  );
  const [failed, setFailed] = useState(!!stored.pending);
  const uncertain = useRef(!!stored.pending);
  const conversation = detail.data;

  const refresh = useCallback(() => {
    void qc.invalidateQueries({
      queryKey: [QueryKeys.MessagingConversations, scope],
    });
    void qc.invalidateQueries({ queryKey: [QueryKeys.MessagingUnread, scope] });
  }, [qc, scope]);
  const send = useMutation({
    mutationFn: (payload: SendMessagingMessagePayload) => api.send(id, payload),
    onSuccess: ({ message }) => {
      qc.setQueryData<InfiniteData<MessagingPage<MessagingMessage>>>(
        historyKey,
        (previous) =>
          previous
            ? {
                ...previous,
                pages: previous.pages.map((page, index) =>
                  index === 0
                    ? {
                        ...page,
                        items: mergeMessagingMessages([...page.items, message]),
                      }
                    : page
                ),
              }
            : {
                pages: [{ items: [message], nextCursor: null }],
                pageParams: [null],
              }
      );
      if (!mounted.current) {
        refresh();
        void qc.invalidateQueries({ queryKey: historyKey });
        return;
      }
      setText('');
      setStorageAvailable(saveMessagingDraft(scope, id, { text: '' }));
      setFailed(false);
      submission.current = undefined;
      uncertain.current = false;
      atBottom.current = true;
      refresh();
      void qc.invalidateQueries({ queryKey: historyKey });
    },
    onError: (error, variables) => {
      if (!mounted.current) return;
      // History may already have confirmed this ID before its HTTP ack failed.
      if (submission.current?.clientMessageId !== variables.clientMessageId)
        return;
      if (isKnownMessagingRejection(error) && !uncertain.current)
        submission.current = undefined;
      else uncertain.current = true;
      setStorageAvailable(
        saveMessagingDraft(scope, id, { text, pending: submission.current })
      );
      setFailed(true);
    },
  });

  const sendMessage = useCallback(() => {
    const trimmed = text.trim();
    if (
      !trimmed ||
      trimmed.length > 4000 ||
      (!conversation?.canSend && !submission.current) ||
      send.isPending ||
      connection === 'offline'
    )
      return;
    submission.current ??= messagingDraft(trimmed);
    setStorageAvailable(
      saveMessagingDraft(scope, id, { text, pending: submission.current })
    );
    send.mutate(submission.current);
  }, [connection, conversation?.canSend, send, text, id, scope]);

  useEffect(() => {
    setStorageAvailable(
      saveMessagingDraft(scope, id, { text, pending: submission.current })
    );
  }, [scope, id, text]);
  useEffect(() => {
    if (
      submission.current &&
      rows.some(
        (row) =>
          row.clientMessageId === submission.current?.clientMessageId &&
          row.senderUserId === bootstrap?.userId
      )
    ) {
      submission.current = undefined;
      uncertain.current = false;
      setText('');
      setFailed(false);
      setStorageAvailable(saveMessagingDraft(scope, id, { text: '' }));
    }
  }, [bootstrap?.userId, id, rows, scope]);

  const lastId = rows.at(-1)?.id;
  useEffect(() => {
    if (atBottom.current && scroll.current)
      scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [lastId]);

  // Observe incoming bubbles themselves. Background fetches and messages below
  // the visible viewport never advance the read marker.
  useEffect(() => {
    const root = scroll.current;
    if (
      !root ||
      !bootstrap ||
      !conversation ||
      typeof IntersectionObserver === 'undefined'
    )
      return;
    const visible = new Map<Element, string>();
    let disposed = false;
    let sendingRead = false;
    const markVisible = async () => {
      if (
        disposed ||
        sendingRead ||
        document.visibilityState !== 'visible' ||
        !document.hasFocus() ||
        visible.size === 0
      )
        return;
      const through = [...visible.values()]
        .sort(compareMessagingSequence)
        .at(-1)!;
      const currentRead =
        compareMessagingSequence(
          readThrough.current,
          conversation.lastReadSequence
        ) > 0
          ? readThrough.current
          : conversation.lastReadSequence;
      if (compareMessagingSequence(through, currentRead) <= 0) return;
      sendingRead = true;
      try {
        const result = await api.read(id, through);
        if (!disposed) {
          readThrough.current = result.lastReadSequence;
          refresh();
        }
      } catch {
        /* A later viewport, focus or poll retries the read. */
      } finally {
        sendingRead = false;
      }
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sequence = (entry.target as HTMLElement).dataset.sequence;
          if (
            entry.isIntersecting &&
            messagingBubbleIsVisible(
              entry.intersectionRect?.height ?? 0,
              entry.boundingClientRect?.height ?? 0,
              root.clientHeight
            ) &&
            sequence
          )
            visible.set(entry.target, sequence);
          else visible.delete(entry.target);
        });
        void markVisible();
      },
      { root, threshold: [0, 0.01, 1] }
    );
    root
      .querySelectorAll('[data-incoming="true"]')
      .forEach((element) => observer.observe(element));
    const focus = () => {
      void markVisible();
    };
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', focus);
    const retry = window.setInterval(focus, 5_000);
    return () => {
      disposed = true;
      observer.disconnect();
      window.clearInterval(retry);
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', focus);
    };
  }, [api, bootstrap, conversation, id, refresh, rows]);

  if (detail.isLoading)
    return (
      <div className={styles.state} role="status">
        {t('messaging.loading')}
      </div>
    );
  if (detail.isError || !conversation)
    return (
      <div className={styles.state} role="alert">
        <p>{t('messaging.error')}</p>
        <Button
          onClick={() => {
            void detail.refetch();
          }}
        >
          {t('messaging.retry')}
        </Button>
        <Button variant="outline" onClick={onBack}>
          {t('messaging.back')}
        </Button>
      </div>
    );
  return (
    <>
      <header className={styles.threadHeader}>
        <Button
          variant="ghost"
          className={styles.back}
          aria-label={t('messaging.back')}
          onClick={onBack}
        >
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h2>{conversation.peer.displayName}</h2>
          <p>{t(`messaging.recipient_role_${conversation.peer.role}`)}</p>
        </div>
      </header>
      <div
        ref={scroll}
        className={styles.messages}
        role="log"
        aria-label={conversation.peer.displayName}
        aria-live="polite"
        onScroll={() => {
          const element = scroll.current;
          if (element)
            atBottom.current =
              element.scrollHeight - element.scrollTop - element.clientHeight <
              64;
        }}
      >
        {history.hasNextPage && (
          <Button
            variant="outline"
            disabled={history.isFetchingNextPage}
            onClick={() => {
              const element = scroll.current;
              const oldHeight = element?.scrollHeight ?? 0;
              void history.fetchNextPage().then(() => {
                requestAnimationFrame(() => {
                  if (element)
                    element.scrollTop += element.scrollHeight - oldHeight;
                });
              });
            }}
          >
            {t('messaging.older')}
          </Button>
        )}
        {history.isLoading && (
          <p role="status" className={styles.muted}>
            {t('messaging.loading')}
          </p>
        )}
        {history.isError && (
          <div role="alert">
            <p>{t('messaging.error')}</p>
            <Button
              onClick={() => {
                void history.refetch();
              }}
            >
              {t('messaging.retry')}
            </Button>
          </div>
        )}
        {!history.isLoading && !history.isError && rows.length === 0 && (
          <p className={styles.muted}>{t('messaging.empty_messages')}</p>
        )}
        {rows.map((message) => {
          const own = message.senderUserId === bootstrap?.userId;
          const read = !!settings?.readReceipts && !!message.readAt;
          return (
            <article
              key={message.id}
              className={`${styles.bubble} ${own ? styles.own : ''}`}
              data-sequence={message.sequence}
              data-incoming={own ? undefined : 'true'}
            >
              <p className={styles.body}>{message.text}</p>
              <div className={styles.meta}>
                <time dateTime={message.createdAt}>
                  {formatDate(message.createdAt, {
                    format: 'mediumTime',
                    locale: i18n.language,
                  })}
                </time>
                {own && (
                  <span>
                    {read ? (
                      <CheckCheck size={12} aria-hidden />
                    ) : (
                      <Check size={12} aria-hidden />
                    )}
                    {t(read ? 'messaging.read' : 'messaging.sent')}
                  </span>
                )}
              </div>
            </article>
          );
        })}
      </div>
      <form
        className={styles.composer}
        onSubmit={(event) => {
          event.preventDefault();
          sendMessage();
        }}
      >
        {!storageAvailable && (
          <p role="status" className={styles.muted}>
            {t('messaging.draft_not_saved')}
          </p>
        )}
        {!conversation.canSend && (
          <p className={styles.muted} role="status">
            {t('messaging.send_blocked')}
          </p>
        )}
        {failed && (
          <div role="alert" className={styles.banner}>
            <span>
              {t(
                submission.current
                  ? 'messaging.uncertain_send'
                  : 'messaging.failed_send'
              )}
            </span>
            {!submission.current && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setFailed(false)}
              >
                {t('messaging.edit_message')}
              </Button>
            )}
          </div>
        )}
        <textarea
          ref={textarea}
          aria-label={t('messaging.write')}
          placeholder={t('messaging.write')}
          value={text}
          maxLength={4000}
          disabled={
            !conversation.canSend || send.isPending || !!submission.current
          }
          onChange={(event) => setText(event.target.value)}
        />
        <div className={styles.composerFooter}>
          <div className={styles.composerTools}>
            <EmojiPicker
              textareaRef={textarea}
              value={text}
              onChange={setText}
              disabled={
                !conversation.canSend ||
                send.isPending ||
                !!submission.current ||
                connection === 'offline'
              }
            />
            <p>
              {t('messaging.message_length', { count: text.length, max: 4000 })}
            </p>
          </div>
          <Button
            type="submit"
            IconSuffix={Send}
            disabled={
              (!conversation.canSend && !submission.current) ||
              !text.trim() ||
              text.trim().length > 4000 ||
              send.isPending ||
              connection === 'offline'
            }
          >
            {t(
              send.isPending
                ? 'messaging.sending'
                : failed
                  ? 'messaging.retry_send'
                  : 'messaging.send'
            )}
          </Button>
        </div>
      </form>
    </>
  );
};
